// Module couple — « Ce que je crois savoir de nous » (Exercice 1 : chacun devine pour l'autre).
// Non scoré. Bilingue FR/ES. Deux partenaires remplissent chacun leur feuille, puis une vue centralisée compare.

export const EMPATHIE_DEFINITION = {
  fr: "L'empathie est le fondement d'une relation épanouie : cette capacité précieuse à écouter non pas pour répondre, convaincre ou se défendre, mais pour comprendre. Cet exercice n'est pas un test — c'est un pont entre deux perspectives qui parfois se perdent de vue.",
  es: "La empatía es la base de una relación plena: esa preciosa capacidad de escuchar no para responder, convencer o defenderse, sino para comprender. Este ejercicio no es una prueba, es un puente entre dos perspectivas que a veces se pierden de vista.",
};

export const EMPATHIE_CONSIGNE = {
  fr: [
    "Chacun va deviner pour l'autre plusieurs aspects importants de sa perception et de son expérience au sein du couple.",
    "Installez-vous dans un moment calme. Chacun remplit sa feuille sans regarder les réponses de l'autre, avant de les partager.",
    "Il n'y a pas de bonnes ou de mauvaises réponses — seulement des occasions d'apprendre et de se rapprocher. Un écart entre vos suppositions et la réalité n'est pas un échec, mais une information précieuse sur les angles morts de votre relation.",
  ],
  es: [
    "Cada uno va a adivinar por el otro varios aspectos importantes de su percepción y su experiencia dentro de la pareja.",
    "Busquen un momento tranquilo. Cada uno rellena su hoja sin mirar las respuestas del otro, antes de compartirlas.",
    "No hay respuestas buenas ni malas, solo ocasiones para aprender y acercarse. Una diferencia entre sus suposiciones y la realidad no es un fracaso, sino una información valiosa sobre los puntos ciegos de su relación.",
  ],
};

export const EMPATHIE_DIMENSIONS = [
  {
    key: "moments",
    title: { fr: "Ses 3 meilleurs moments durant le couple", es: "Sus 3 mejores momentos en la pareja" },
    intro: {
      fr: "Certains instants brillent plus fort que d'autres dans notre mémoire et deviennent les piliers émotionnels de la relation.",
      es: "Algunos instantes brillan más que otros en nuestra memoria y se convierten en los pilares emocionales de la relación.",
    },
    guessLabel: { fr: "Selon vous, les 3 meilleurs moments de votre partenaire", es: "Según usted, los 3 mejores momentos de su pareja" },
    realityLabel: { fr: "Vos 3 meilleurs moments à vous", es: "Sus 3 mejores momentos" },
    hint: { fr: "Un moment par ligne, aussi précis que possible.", es: "Un momento por línea, lo más preciso posible." },
  },
  {
    key: "fiertes",
    title: { fr: "Ses fiertés, ses réussites", es: "Sus orgullos, sus logros" },
    intro: {
      fr: "Chacun porte des accomplissements dont il est fier — succès éclatants ou victoires plus discrètes, mais tout aussi significatives.",
      es: "Cada uno lleva consigo logros de los que se siente orgulloso, éxitos brillantes o victorias más discretas pero igual de significativas.",
    },
    guessLabel: { fr: "Selon vous, ses 3 plus grandes fiertés / réussites", es: "Según usted, sus 3 mayores orgullos / logros" },
    realityLabel: { fr: "Vos propres fiertés / réussites", es: "Sus propios orgullos / logros" },
    hint: { fr: "Une par ligne.", es: "Uno por línea." },
  },
  {
    key: "plaisirs",
    title: { fr: "3 choses du quotidien qu'il/elle apprécie", es: "3 cosas del día a día que aprecia" },
    intro: {
      fr: "Le bonheur réside souvent dans les petites choses du quotidien qui ressourcent et donnent de l'énergie.",
      es: "La felicidad reside a menudo en las pequeñas cosas del día a día que reconfortan y dan energía.",
    },
    guessLabel: { fr: "Selon vous, les 3 petits plaisirs quotidiens de votre partenaire", es: "Según usted, los 3 pequeños placeres cotidianos de su pareja" },
    realityLabel: { fr: "Vos 3 petits plaisirs quotidiens", es: "Sus 3 pequeños placeres cotidianos" },
    hint: { fr: "Un plaisir par ligne.", es: "Un placer por línea." },
  },
  {
    key: "frustrations",
    title: { fr: "Ce qu'il/elle ne supporte pas/plus chez moi", es: "Lo que no soporta / ya no soporta de mí" },
    intro: {
      fr: "La partie la plus délicate, mais la plus libératrice : le danger réside dans le non-dit. Devinez ce qui, chez vous, agace votre partenaire.",
      es: "La parte más delicada, pero la más liberadora: el peligro está en lo no dicho. Adivine qué, en usted, molesta a su pareja.",
    },
    guessLabel: { fr: "Ce qui, selon vous, agace votre partenaire chez vous", es: "Lo que, según usted, molesta a su pareja de usted" },
    realityLabel: { fr: "Ce que vous ne supportez pas / plus chez votre partenaire", es: "Lo que usted no soporta / ya no soporta de su pareja" },
    hint: { fr: "Soyez honnête et bienveillant.", es: "Sea honesto y benevolente." },
  },
  {
    key: "besoin_entendre",
    title: { fr: "Ce que j'ai besoin d'entendre ou que je n'entends pas assez", es: "Lo que necesito oír o que no oigo lo suficiente" },
    intro: {
      fr: "Certains mots, certaines reconnaissances nous manquent parfois sans qu'on ose les demander. Les identifier, c'est ouvrir la porte à plus de tendresse.",
      es: "A veces nos faltan ciertas palabras o reconocimientos sin que nos atrevamos a pedirlos. Identificarlos abre la puerta a más ternura.",
    },
    guessLabel: { fr: "Selon vous, ce que votre partenaire a besoin d'entendre (ou n'entend pas assez)", es: "Según usted, lo que su pareja necesita oír (o no oye lo suficiente)" },
    realityLabel: { fr: "Ce que vous, vous avez besoin d'entendre (ou n'entendez pas assez)", es: "Lo que usted necesita oír (o no oye lo suficiente)" },
    hint: { fr: "Une idée par ligne.", es: "Una idea por línea." },
  },
  {
    key: "trop_entendre",
    title: { fr: "Ce que je n'ai pas besoin d'entendre ou que j'entends de trop", es: "Lo que no necesito oír o que oigo demasiado" },
    intro: {
      fr: "À l'inverse, certaines phrases répétées pèsent ou blessent. Les nommer permet d'alléger la relation.",
      es: "Por el contrario, ciertas frases repetidas pesan o hieren. Nombrarlas permite aligerar la relación.",
    },
    guessLabel: { fr: "Selon vous, ce que votre partenaire n'a pas besoin d'entendre (ou entend de trop)", es: "Según usted, lo que su pareja no necesita oír (o oye demasiado)" },
    realityLabel: { fr: "Ce que vous, vous n'avez pas besoin d'entendre (ou entendez de trop)", es: "Lo que usted no necesita oír (o oye demasiado)" },
    hint: { fr: "Une idée par ligne.", es: "Una idea por línea." },
  },
];

// Guide de dialogue affiché sous chaque écart de la version centralisée.
export const EMPATHIE_GUIDE = {
  fr: {
    moments: [
      "Qu'est-ce qui vous a surpris dans les moments cités par l'autre ?",
      "Comment pourriez-vous recréer davantage ce type de moments ensemble ?",
    ],
    fiertes: [
      "Aviez-vous conscience de ces fiertés ? Comment mieux les reconnaître au quotidien ?",
      "De quelle manière pouvez-vous soutenir l'autre dans ce qui compte pour lui/elle ?",
    ],
    plaisirs: [
      "Comment protéger et faciliter ces petits plaisirs pour l'autre ?",
      "Y a-t-il un plaisir que vous aimeriez partager davantage ensemble ?",
    ],
    frustrations: [
      "En écoutant l'autre, qu'avez-vous appris — sans vous justifier ?",
      "Quel premier petit changement concret pourriez-vous proposer ?",
    ],
    besoin_entendre: [
      "Qu'avez-vous ressenti en découvrant ce que l'autre a besoin d'entendre ?",
      "Comment pourriez-vous offrir ces mots plus souvent, avec sincérité ?",
    ],
    trop_entendre: [
      "Y a-t-il des phrases que vous répétez sans en mesurer l'effet ?",
      "Quel mot pourriez-vous remplacer par une formulation plus douce ?",
    ],
  },
  es: {
    moments: [
      "¿Qué le sorprendió de los momentos que mencionó el otro?",
      "¿Cómo podrían recrear más este tipo de momentos juntos?",
    ],
    fiertes: [
      "¿Era consciente de estos orgullos? ¿Cómo reconocerlos mejor en el día a día?",
      "¿De qué manera puede apoyar al otro en lo que le importa?",
    ],
    plaisirs: [
      "¿Cómo proteger y facilitar estos pequeños placeres para el otro?",
      "¿Hay un placer que le gustaría compartir más juntos?",
    ],
    frustrations: [
      "Al escuchar al otro, ¿qué aprendió, sin justificarse?",
      "¿Qué primer pequeño cambio concreto podría proponer?",
    ],
    besoin_entendre: [
      "¿Qué sintió al descubrir lo que el otro necesita oír?",
      "¿Cómo podría ofrecer esas palabras más a menudo, con sinceridad?",
    ],
    trop_entendre: [
      "¿Hay frases que repite sin medir su efecto?",
      "¿Qué palabra podría sustituir por una formulación más suave?",
    ],
  },
};

// Exercice 2 : gestion approfondie de la frustration.
export const FRUSTRATION_SECTIONS = [
  {
    title: { fr: "Ma description de la situation", es: "Mi descripción de la situación" },
    fields: [
      { key: "situation", label: { fr: "Décrivez factuellement une situation récente de frustration (les faits observables, sans interprétation).", es: "Describa de forma factual una situación reciente de frustración (los hechos observables, sin interpretación)." } },
    ],
  },
  {
    title: { fr: "Ce qui était exagéré dans les réactions", es: "Lo que fue exagerado en las reacciones" },
    fields: [
      { key: "exag_pensees", label: { fr: "En moi — Pensées disproportionnées", es: "En mí — Pensamientos desproporcionados" } },
      { key: "exag_emotions", label: { fr: "En moi — Émotions amplifiées", es: "En mí — Emociones amplificadas" } },
      { key: "exag_comportements", label: { fr: "En moi — Comportements excessifs", es: "En mí — Comportamientos excesivos" } },
      { key: "exag_autre", label: { fr: "Chez l'autre — ce qui m'a semblé exagéré (avec bienveillance)", es: "En el otro — lo que me pareció exagerado (con benevolencia)" } },
    ],
  },
  {
    title: { fr: "Ce qui était légitime (non exagéré)", es: "Lo que fue legítimo (no exagerado)" },
    fields: [
      { key: "legit_pensees", label: { fr: "En moi — Pensées légitimes et proportionnées", es: "En mí — Pensamientos legítimos y proporcionados" } },
      { key: "legit_emotions", label: { fr: "En moi — Émotions justifiées et saines", es: "En mí — Emociones justificadas y sanas" } },
      { key: "legit_comportements", label: { fr: "En moi — Comportements appropriés et constructifs", es: "En mí — Comportamientos apropiados y constructivos" } },
      { key: "legit_autre", label: { fr: "Chez l'autre — ce qui était compréhensible et légitime", es: "En el otro — lo que era comprensible y legítimo" } },
    ],
  },
  {
    title: { fr: "Les éléments qui aggravent la frustration", es: "Los elementos que agravan la frustración" },
    fields: [
      { key: "aggr_moi", label: { fr: "Chez moi (fatigue, stress, blessures anciennes, attentes irréalistes…)", es: "En mí (cansancio, estrés, heridas antiguas, expectativas poco realistas…)" } },
      { key: "aggr_autre", label: { fr: "Chez l'autre (stress, incompréhension, réaction défensive…)", es: "En el otro (estrés, incomprensión, reacción defensiva…)" } },
    ],
  },
  {
    title: { fr: "Les éléments qui soulagent la frustration", es: "Los elementos que alivian la frustración" },
    fields: [
      { key: "soul_moi", label: { fr: "Chez moi (temps seul, conversation claire, reconnaissance…)", es: "En mí (tiempo a solas, conversación clara, reconocimiento…)" } },
      { key: "soul_autre", label: { fr: "Chez l'autre (écoute, espace, excuses, réconfort…)", es: "En el otro (escucha, espacio, disculpas, consuelo…)" } },
    ],
  },
  {
    title: { fr: "Les non-dits essentiels", es: "Los no dichos esenciales" },
    fields: [
      { key: "nondit_dire", label: { fr: "Ce que je veux dire mais que je n'ose pas demander", es: "Lo que quiero decir pero no me atrevo a pedir" } },
      { key: "nondit_entendre", label: { fr: "Ce que je veux entendre sans avoir à le demander", es: "Lo que quiero oír sin tener que pedirlo" } },
    ],
  },
  {
    title: { fr: "Ce que l'autre attend de moi", es: "Lo que el otro espera de mí" },
    fields: [
      { key: "att_quoi", label: { fr: "Quoi ? (précis et concret)", es: "¿Qué? (preciso y concreto)" } },
      { key: "att_pourquoi", label: { fr: "Pourquoi ? (le besoin, la peur, la valeur)", es: "¿Por qué? (la necesidad, el miedo, el valor)" } },
      { key: "att_avantages", label: { fr: "Les avantages (pour moi, pour l'autre, pour la relation)", es: "Las ventajas (para mí, para el otro, para la relación)" } },
      { key: "att_inconvenients", label: { fr: "Les inconvénients (coût, temps, énergie)", es: "Los inconvenientes (coste, tiempo, energía)" } },
    ],
  },
  {
    title: { fr: "Plan d'action à réfléchir ensemble", es: "Plan de acción para reflexionar juntos" },
    fields: [
      { key: "plan_vouloir", label: { fr: "VOULOIR — Est-ce que je veux vraiment le faire ?", es: "QUERER — ¿Realmente quiero hacerlo?" } },
      { key: "plan_pouvoir", label: { fr: "POUVOIR — Ai-je le temps, l'énergie, les ressources ?", es: "PODER — ¿Tengo el tiempo, la energía, los recursos?" } },
      { key: "plan_savoir", label: { fr: "SAVOIR — Ai-je les compétences ? Dois-je apprendre ?", es: "SABER — ¿Tengo las competencias? ¿Debo aprender?" } },
      { key: "plan_quand", label: { fr: "QUAND — À quel moment concret ?", es: "CUÁNDO — ¿En qué momento concreto?" } },
    ],
  },
];

export const EMPATHIE_T = {
  fr: {
    allModules: "← Tous les modules",
    welcomeTitle: "Ce que je crois savoir de nous",
    start: "Commencer l'exercice",
    ex1: "Exercice 1",
    ex1Sub: "Chacun doit deviner pour l'autre",
    prev: "Précédent",
    next: "Suivant",
    modeTitle: "Comment souhaitez-vous procéder ?",
    modeSingle: "Sur cet appareil",
    modeSingleDesc: "Vous remplissez chacun votre feuille à tour de rôle sur le même téléphone.",
    modeTwo: "Chacun son téléphone",
    modeTwoDesc: "Reliez vos deux feuilles par un code de séance. Chacun répond de son côté.",
    haveCode: "J'ai déjà un code de séance →",
    optionalExercise: "Exercice optionnel",
    connaisDesc: "Un jeu de découverte inspiré de Gottman : devinez les réponses de l'autre et mesurez votre « score de curiosité ».",
    connaisAfter: "Pour aller plus loin après cet exercice : un jeu de découverte inspiré de Gottman où chacun devine les réponses de l'autre, puis vous mesurez votre « score de curiosité ».",
    connaisBefore: "En complément, un exercice optionnel inspiré de Gottman : chacun devine les réponses de l'autre, puis vous mesurez votre « score de curiosité ». Vous pouvez le découvrir maintenant ou après l'exercice principal.",
    connaisStart: "Commencer l'exercice optionnel",
    setupTitle: "Qui participe ?",
    setupHint: "Indiquez vos prénoms (facultatif). Vous remplirez chacun votre feuille à tour de rôle.",
    partner1: "Partenaire 1",
    partner2: "Partenaire 2",
    partner1Ph: "Prénom du partenaire 1",
    partner2Ph: "Prénom du partenaire 2",
    setupStart: "Commencer avec le partenaire 1",
    sheetOf: "Feuille de",
    sheetHint: (o) => `Répondez sans regarder les réponses de ${o}. Devinez pour l'autre, puis notez votre propre réalité.`,
    realityHint: "Votre vérité à vous.",
    guessPh: "Votre supposition…",
    realityPh: "Votre réalité…",
    toPartner2: "Au tour du partenaire 2",
    seeCentral: "Voir la version centralisée",
    validateSheet: "Valider ma feuille",
    handoffTitle: (n) => `C'est au tour de ${n}`,
    handoffText: (n) => `Passez l'appareil à ${n}. Les réponses du premier partenaire restent cachées jusqu'à la version centralisée.`,
    handoffBtn: (n) => `Je suis ${n}, commencer`,
    createTitle: "Créer une séance",
    createDesc: "Vous obtiendrez un code à partager avec votre partenaire.",
    joinTitle: "Rejoindre avec un code",
    joinDesc: "Votre partenaire a déjà créé une séance et vous a transmis un code.",
    codeLabel: "Code de séance",
    codePh: "Ex. K7M2P9",
    yourName: "Votre prénom",
    yourNamePh: "Votre prénom",
    genCode: "Générer le code",
    join: "Rejoindre",
    yourCode: "Votre code de séance",
    shareCode: "Partagez ce code — ou faites scanner le QR code — pour que votre partenaire rejoigne la séance sur son téléphone.",
    copyCode: "Copier le code",
    codeCopied: "Code copié",
    fillSheet: "Remplir ma feuille",
    waitTitle: "Votre feuille est enregistrée",
    waitText: "En attente que votre partenaire termine sa feuille. La version centralisée s'ouvrira automatiquement dès que vous aurez tous les deux répondu.",
    you: "Vous",
    done: "✓ terminé",
    partner: "Partenaire",
    inProgress: "en cours…",
    centralTitle: "Version centralisée",
    centralText: "Dimension par dimension, la réalité de chacun mise en regard de ce que l'autre avait deviné. Les écarts sont des ponts à construire — parlez-en ensemble à l'aide des questions proposées.",
    guideTitle: "Pour en parler sereinement",
    frustReportTitle: "Travail sur la frustration",
    frustFor: (n) => `Frustration — ${n}`,
    frustMgmt: "Gestion de la frustration",
    exploreFrust: "Explorez ensemble une situation de frustration",
    exploreFrustTwoSuffix: " — chacun peut compléter la sienne à distance via le code de séance",
    frustEdit: "Compléter / modifier la frustration →",
    frustDeepen: "Approfondir : gestion de la frustration →",
    refresh: "Rafraîchir",
    reportRefreshed: "Rapport actualisé",
    refreshFail: "Impossible d'actualiser",
    exportFull: "Exporter le rapport complet",
    restart: "Recommencer",
    seenBy: (s, o) => `${s} vu(e) par ${o}`,
    realityOf: (s) => `Réalité de ${s}`,
    guessedBy: (o) => `Ce que ${o} avait deviné`,
    frustIntro: "La frustration est une attente contrariée. L'objectif n'est pas de trouver un coupable, mais de comprendre les mécanismes et d'identifier des actions concrètes.",
    answerPh: "Votre réponse…",
    saveSeeReport: "Enregistrer et voir le rapport",
    backNoSave: "Retour sans enregistrer",
    createFail: "Impossible de créer la séance",
    enterValidCode: "Entrez un code valide",
    codeNotFound: "Code introuvable",
    joinError: "Erreur de connexion à la séance",
    submitFail: "Échec de l'enregistrement",
    saveFail: "Échec de l'enregistrement — vos réponses n'ont pas été sauvegardées. Réessayez.",
  },
  es: {
    allModules: "← Todos los módulos",
    welcomeTitle: "Lo que creo entender de nosotros",
    start: "Empezar el ejercicio",
    ex1: "Ejercicio 1",
    ex1Sub: "Cada uno debe adivinar por el otro",
    prev: "Anterior",
    next: "Siguiente",
    modeTitle: "¿Cómo desean proceder?",
    modeSingle: "En este dispositivo",
    modeSingleDesc: "Cada uno rellena su hoja por turnos en el mismo teléfono.",
    modeTwo: "Cada uno su teléfono",
    modeTwoDesc: "Conecten sus dos hojas con un código de sesión. Cada uno responde por su lado.",
    haveCode: "Ya tengo un código de sesión →",
    optionalExercise: "Ejercicio opcional",
    connaisDesc: "Un juego de descubrimiento inspirado en Gottman: adivinen las respuestas del otro y midan su «puntuación de curiosidad».",
    connaisAfter: "Para ir más lejos tras este ejercicio: un juego de descubrimiento inspirado en Gottman donde cada uno adivina las respuestas del otro, y luego miden su «puntuación de curiosidad».",
    connaisBefore: "Como complemento, un ejercicio opcional inspirado en Gottman: cada uno adivina las respuestas del otro, y luego miden su «puntuación de curiosidad». Puede descubrirlo ahora o después del ejercicio principal.",
    connaisStart: "Empezar el ejercicio opcional",
    setupTitle: "¿Quién participa?",
    setupHint: "Indiquen sus nombres (opcional). Cada uno rellenará su hoja por turnos.",
    partner1: "Pareja 1",
    partner2: "Pareja 2",
    partner1Ph: "Nombre de la pareja 1",
    partner2Ph: "Nombre de la pareja 2",
    setupStart: "Empezar con la pareja 1",
    sheetOf: "Hoja de",
    sheetHint: (o) => `Responda sin mirar las respuestas de ${o}. Adivine por el otro y luego anote su propia realidad.`,
    realityHint: "Su propia verdad.",
    guessPh: "Su suposición…",
    realityPh: "Su realidad…",
    toPartner2: "Turno de la pareja 2",
    seeCentral: "Ver la versión centralizada",
    validateSheet: "Validar mi hoja",
    handoffTitle: (n) => `Es el turno de ${n}`,
    handoffText: (n) => `Pase el dispositivo a ${n}. Las respuestas de la primera persona permanecen ocultas hasta la versión centralizada.`,
    handoffBtn: (n) => `Soy ${n}, empezar`,
    createTitle: "Crear una sesión",
    createDesc: "Obtendrá un código para compartir con su pareja.",
    joinTitle: "Unirse con un código",
    joinDesc: "Su pareja ya creó una sesión y le transmitió un código.",
    codeLabel: "Código de sesión",
    codePh: "Ej. K7M2P9",
    yourName: "Su nombre",
    yourNamePh: "Su nombre",
    genCode: "Generar el código",
    join: "Unirse",
    yourCode: "Su código de sesión",
    shareCode: "Comparta este código — o haga escanear el código QR — para que su pareja se una a la sesión desde su teléfono.",
    copyCode: "Copiar el código",
    codeCopied: "Código copiado",
    fillSheet: "Rellenar mi hoja",
    waitTitle: "Su hoja está guardada",
    waitText: "Esperando a que su pareja termine su hoja. La versión centralizada se abrirá automáticamente cuando ambos hayan respondido.",
    you: "Usted",
    done: "✓ terminado",
    partner: "Pareja",
    inProgress: "en curso…",
    centralTitle: "Versión centralizada",
    centralText: "Dimensión por dimensión, la realidad de cada uno frente a lo que el otro había adivinado. Las diferencias son puentes por construir: hablen de ello juntos con ayuda de las preguntas propuestas.",
    guideTitle: "Para hablar de ello con serenidad",
    frustReportTitle: "Trabajo sobre la frustración",
    frustFor: (n) => `Frustración — ${n}`,
    frustMgmt: "Gestión de la frustración",
    exploreFrust: "Exploren juntos una situación de frustración",
    exploreFrustTwoSuffix: " — cada uno puede completar la suya a distancia mediante el código de sesión",
    frustEdit: "Completar / modificar la frustración →",
    frustDeepen: "Profundizar: gestión de la frustración →",
    refresh: "Actualizar",
    reportRefreshed: "Informe actualizado",
    refreshFail: "No se puede actualizar",
    exportFull: "Exportar el informe completo",
    restart: "Volver a empezar",
    seenBy: (s, o) => `${s} visto/a por ${o}`,
    realityOf: (s) => `Realidad de ${s}`,
    guessedBy: (o) => `Lo que ${o} había adivinado`,
    frustIntro: "La frustración es una expectativa contrariada. El objetivo no es encontrar un culpable, sino comprender los mecanismos e identificar acciones concretas.",
    answerPh: "Su respuesta…",
    saveSeeReport: "Guardar y ver el informe",
    backNoSave: "Volver sin guardar",
    createFail: "No se puede crear la sesión",
    enterValidCode: "Introduzca un código válido",
    codeNotFound: "Código no encontrado",
    joinError: "Error de conexión a la sesión",
    submitFail: "Error al guardar",
    saveFail: "Error al guardar — sus respuestas no se han guardado. Inténtelo de nuevo.",
  },
};
